import { defineStore } from "pinia";
import { ref } from "vue";
import { requestAssignmentStore } from "@/modules/practitionerWorkbench/store/requestAssignmentStore";
import { useUserProfileStore } from "@/modules/userProfile/store/userProfileStore";

export const useNavigationStore = defineStore("navigation", () => {
  // state
  const navPathList = ref<string[]>([]);
  const userProfileStore = useUserProfileStore();
  const userRole = userProfileStore?.displayUserRole?.split(", ").map((role) => role.trim());

  // actions
  const setTraversePath = (toRoute, fromRoute) => {
    if (userRole?.includes("ADMIN")) {
      setAdminTraversePath(toRoute, fromRoute);
    } else if (userRole?.includes("LEGAL_PRACTITIONER")) {
      setPractitionerTraversePath();
    } else {
      setEmployeeTraversePath();
    }

    // Add the route name if it's not already in the list
    if (!navPathList.value.includes(toRoute.name)) {
      navPathList.value.push(toRoute.name);
    }
  };

  // This function sets the breadcrumb path for admin users based on the selected role
  // and the current route. It updates the navigation path list accordingly.
  function setAdminTraversePath(toRoute, fromRoute) {
    switch (requestAssignmentStore.selectedDropdownRole) {
      case "EMPLOYEE":
        updateBreadcrumb(toRoute, fromRoute, "AdminHubView", "Practitioner");
        break;
      case "PRACTITIONER":
        updateBreadcrumb(toRoute, fromRoute, "Practitioner", "Home");
        break;
      default:
        updateBreadcrumb(toRoute, fromRoute, "AdminHubView", "Practitioner", true);
        break;
    }
  }

  function setPractitionerTraversePath() {
    if (requestAssignmentStore.selectedDropdownRole === "EMPLOYEE") {
      updatePractitionerBreadcrumbs("Practitioner", "AdminHubView");
    } else {
      updatePractitionerBreadcrumbs("Practitioner", "AdminHubView", true);
    }
  }

  function setEmployeeTraversePath() {
    // If the user is an employee, we only want to keep the Home route in the breadcrumb
    if (navPathList.value.length > 1) {
      navPathList.value.pop();
    }
  }

  function updateBreadcrumb(toRoute, fromRoute, include: string, exclude: string, isAdmin = false) {
    // Back button login
    if (fromRoute.name === "Home" && toRoute.name === "Practitioner") {
      exclude = "Home";
    } else if (fromRoute.name === "Practitioner" && toRoute.name === "Home") {
      exclude = "Practitioner";
    }
    // Remove the excluded route from the list
    navPathList.value = navPathList.value.filter((item) => item !== exclude);

    // Add the new route to the list if it's not already there
    if (!navPathList.value.includes("AdminHubView")) {
      navPathList.value.push("AdminHubView");
    }

    if (requestAssignmentStore.selectedDropdownRole !== "PRACTITIONER") {
      if (!navPathList.value.includes(include)) {
        navPathList.value.unshift(include);
      }
    }
    const maxLength = isAdmin ? 1 : 2;
    if (navPathList.value.length > maxLength) {
      navPathList.value.pop();
    }
  }

  function updatePractitionerBreadcrumbs(include: string, exclude: string, isAdmin = false) {
    navPathList.value = navPathList.value.filter((item) => item !== exclude);

    if (!navPathList.value.includes(include)) {
      navPathList.value.unshift(include);
    }

    const maxLength = isAdmin ? 1 : 2;
    if (navPathList.value.length > maxLength) {
      navPathList.value.pop();
    }
  }

  return {
    navPathList,
    setTraversePath,
    userRole,
    setAdminTraversePath,
    setPractitionerTraversePath,
    setEmployeeTraversePath,
    updateBreadcrumb,
    updatePractitionerBreadcrumbs
  };
});
